import ConfirmModal from "@app/components/confirm-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import BankTransferStatementService from "@app/services/http/bank-transfer-statement.service";
import { openPortalDialog } from "@app/services/modal.service";
import { addToast } from "@app/components/toast/toast.service";
import { Meta } from "@app/types";
import { BankTransferStatement } from "@app/types/bank-transfer-statement.type";
import { formatMoneyVN } from "@app/utils/util";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";

function IdColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.id}</div>;
}

function UserIdColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.userId}</div>;
}

function FullNameColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.fullName}</div>;
}

function TransferHistoryIdColumnTable({
  data,
}: {
  data: BankTransferStatement;
}) {
  return <div>{data.transferHistoryId}</div>;
}

function BankNameColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.bankName}</div>;
}

function BankNumberColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.bankNumber}</div>;
}

function BankOwnerColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.bankOwner}</div>;
}

function AmountColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{formatMoneyVN(data.amount)} đ</div>;
}

function IdentityNumberColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.identityNumber}</div>;
}

function IssuedDateColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.issuedDate}</div>;
}

function IssuedPlaceColumnTable({ data }: { data: BankTransferStatement }) {
  return <div>{data.issuedPlace}</div>;
}

function BeneficiaryAddressColumnTable({
  data,
}: {
  data: BankTransferStatement;
}) {
  return <div>{data.beneficiaryAddress}</div>;
}

const columnConfig: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "id",
    width: "80px",
    component: IdColumnTable,
    hasOrder: true,
  },
  {
    label: "User ID",
    dataKey: "userId",
    width: "100px",
    component: UserIdColumnTable,
    hasOrder: true,
  },
  {
    label: "Họ và tên",
    dataKey: "fullName",
    width: "180px",
    component: FullNameColumnTable,
    hasOrder: true,
  },
  {
    label: "Transfer History ID",
    dataKey: "transferHistoryId",
    width: "150px",
    component: TransferHistoryIdColumnTable,
    hasOrder: true,
  },
  {
    label: "Ngân hàng",
    dataKey: "bankName",
    width: "150px",
    component: BankNameColumnTable,
    hasOrder: true,
  },
  {
    label: "Số tài khoản",
    dataKey: "bankNumber",
    width: "180px",
    component: BankNumberColumnTable,
    hasOrder: true,
  },
  {
    label: "Chủ tài khoản",
    dataKey: "bankOwner",
    width: "180px",
    component: BankOwnerColumnTable,
    hasOrder: true,
  },
  {
    label: "Số tiền",
    dataKey: "amount",
    width: "150px",
    component: AmountColumnTable,
    hasOrder: true,
  },
  {
    label: "CMND/CCCD",
    dataKey: "identityNumber",
    width: "150px",
    component: IdentityNumberColumnTable,
    hasOrder: true,
  },
  {
    label: "Ngày cấp",
    dataKey: "issuedDate",
    width: "150px",
    component: IssuedDateColumnTable,
    hasOrder: true,
  },
  {
    label: "Nơi cấp",
    dataKey: "issuedPlace",
    width: "200px",
    component: IssuedPlaceColumnTable,
    hasOrder: true,
  },
  {
    label: "Địa chỉ người thụ hưởng",
    dataKey: "beneficiaryAddress",
    width: "250px",
    component: BeneficiaryAddressColumnTable,
    hasOrder: true,
  },
];

function BankTransferStatementManagement() {
  const [statements, setStatements] = useState<BankTransferStatement[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );

  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    BankTransferStatementService.getList(
      requestParams,
      abortController.signal
    ).then((res) => {
      setStatements(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleDeleteClick = (statementId: number) => {
    if (statementId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: "Bạn có chắc chắn muốn xóa bảng kê này?",
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          BankTransferStatementService.delete(statementId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  const handleExportClick = () => {
    // TODO: Implement export functionality
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Bảng kê chuyển khoản</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleExportClick}
          >
            <div className="flex gap-4">
              <div>Export Bảng Kê</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Bảng kê chuyển khoản"
          onInputChange={onSearchChange}
        />
        <div className="mt-6 overflow-x-auto">
          <Table
            columnConfig={columnConfig}
            dataTable={statements}
            hasAction
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default BankTransferStatementManagement;
